import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Body5, Body7, Container, Select, TextField } from '@ui';
import { FlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { SelectableCard } from '@ui/SelectableCard';
import { showSuccessBanner } from '@utils/application';
import { mapUnmappedRow } from '@services/fmdc';
import { useVocabularies, vocabularyOptions } from './FmdcTenantContext';
import { CatalogItemPicker } from './CatalogItemPicker';
import { NOTICE_TONES } from './NoticeText';
import { formatCatalogStatus, humanizeToken } from './format';
import { Card, Mono, SectionHead } from './styles';

// Map an unmapped row to a catalog item: one of the candidates the extractor found, the code it
// proposed (which also adds the item to the catalog), or any other item found by search. The values
// appear only after a rerun, and the parent says so from the response's `applies: "on rerun"`.

export const MAP_CHOICES = { CANDIDATE: 'candidate', PROPOSED: 'proposed', OTHER: 'other' };

const CardGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

// The request body for a choice: the proposed code is accepted by flag, everything else by code.
export const mapRequestBody = (choice, fields = {}) => {
  if (!choice) return null;
  const body = choice.kind === MAP_CHOICES.PROPOSED ? { accept_proposed: true } : { canonical_item_code: choice.code };
  if (!choice.code && choice.kind !== MAP_CHOICES.PROPOSED) return null;
  if (fields.split_type) body.split_type = fields.split_type;
  if (fields.split_label) body.split_label = fields.split_label;
  if (fields.metric_component) body.metric_component = fields.metric_component;
  if (fields.reason) body.reason = fields.reason;
  return body;
};

export const MapForm = ({ row, candidateItems = [], onMapped }) => {
  const dispatch = useDispatch();
  const { vocabularies, error: vocabulariesError, isLoading: vocabulariesLoading } = useVocabularies();

  const [choice, setChoice] = React.useState(null);
  const [otherCode, setOtherCode] = React.useState('');
  const [splitType, setSplitType] = React.useState('');
  const [splitLabel, setSplitLabel] = React.useState('');
  const [metricComponent, setMetricComponent] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // A row with no catalog candidates but a candidate code still has something to offer.
  const candidates = React.useMemo(() => {
    if (candidateItems.length > 0) return candidateItems;
    if (row?.candidate_item_code) {
      return [{ canonical_item_code: row.candidate_item_code, canonical_item_label: row.candidate_item_code }];
    }
    return [];
  }, [candidateItems, row?.candidate_item_code]);

  const proposedCode = row?.proposed_new_catalog_code || null;

  const resolvedChoice =
    choice?.kind === MAP_CHOICES.OTHER ? (otherCode ? { kind: MAP_CHOICES.OTHER, code: otherCode } : null) : choice;
  const body = mapRequestBody(resolvedChoice, {
    split_type: splitType,
    split_label: splitLabel.trim(),
    metric_component: metricComponent,
    reason: reason.trim(),
  });
  const canSubmit = !!body && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const result = await mapUnmappedRow(row.id, body);
    setBusy(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    showSuccessBanner('Row mapped', null, dispatch);
    const notices = [];
    if (result.result?.mapping_saved === false) {
      notices.push({
        key: 'mapping',
        tone: NOTICE_TONES.WARNING,
        text: `Mapped, but not saved for the issuer${
          result.result.mapping_note ? `: ${result.result.mapping_note}` : '.'
        }`,
      });
    }
    if (onMapped) onMapped(result.result, notices);
  };

  const isPicked = (kind, code) =>
    resolvedChoice?.kind === kind && (kind === MAP_CHOICES.PROPOSED || choice?.code === code);
  const isOtherOpen = choice?.kind === MAP_CHOICES.OTHER;

  return (
    <Card>
      <SectionHead
        heading="Map this row"
        subtext="Pick the catalog item these cells belong to. The values appear after a rerun."
      />

      <Container top={16}>
        <CardGroup role="radiogroup" aria-label="Catalog item">
          {candidates.map((item) => (
            <SelectableCard
              key={item.canonical_item_code}
              selected={isPicked(MAP_CHOICES.CANDIDATE, item.canonical_item_code)}
            >
              <input
                type="radio"
                name="fmdc-map-choice"
                aria-label={item.canonical_item_label || item.canonical_item_code}
                checked={isPicked(MAP_CHOICES.CANDIDATE, item.canonical_item_code)}
                onChange={() => setChoice({ kind: MAP_CHOICES.CANDIDATE, code: item.canonical_item_code })}
              />
              <CardBody>
                <Body5 bold>{item.canonical_item_label || item.canonical_item_code}</Body5>
                <Mono>{item.canonical_item_code}</Mono>
                {(item.section || item.status) && (
                  <Body7 isLowContrast>
                    {[
                      item.section ? humanizeToken(item.section) : null,
                      item.status ? formatCatalogStatus(item.status) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Body7>
                )}
              </CardBody>
            </SelectableCard>
          ))}

          {proposedCode && (
            <SelectableCard
              selected={isPicked(MAP_CHOICES.PROPOSED)}
              reason="Approves the row's catalog proposal and adds the item to the catalog."
            >
              <input
                type="radio"
                name="fmdc-map-choice"
                aria-label={`Accept proposed code ${proposedCode}`}
                checked={isPicked(MAP_CHOICES.PROPOSED)}
                onChange={() => setChoice({ kind: MAP_CHOICES.PROPOSED, code: proposedCode })}
              />
              <CardBody>
                <Body5 bold>Accept proposed code</Body5>
                <Mono>{proposedCode}</Mono>
              </CardBody>
            </SelectableCard>
          )}

          <SelectableCard selected={isOtherOpen}>
            <input
              type="radio"
              name="fmdc-map-choice"
              aria-label="Another item"
              checked={isOtherOpen}
              onChange={() => setChoice({ kind: MAP_CHOICES.OTHER, code: otherCode })}
            />
            <CardBody>
              <Body5 bold>Another item…</Body5>
              <Body7 isLowContrast>Search the catalog for the item these cells belong to.</Body7>
            </CardBody>
          </SelectableCard>
        </CardGroup>
      </Container>

      {isOtherOpen && (
        <Container top={16}>
          <CatalogItemPicker label="Catalog item" value={otherCode} onChange={(code) => setOtherCode(code)} autoFocus />
        </Container>
      )}

      {vocabulariesLoading && (
        <Container top={12}>
          <Body7 isLowContrast role="status">
            Loading vocabularies…
          </Body7>
        </Container>
      )}
      {vocabulariesError && (
        <Container top={12}>
          <ErrorText>{vocabulariesError}</ErrorText>
        </Container>
      )}

      <Container top={16}>
        <Select
          label="Split type"
          value={splitType}
          onChange={setSplitType}
          options={vocabularyOptions(vocabularies, 'split_type')}
          placeholder="None"
        />
      </Container>
      <Container top={16}>
        <TextField label="Split label" value={splitLabel} onChange={setSplitLabel} placeholder="Optional" />
      </Container>
      <Container top={16}>
        <Select
          label="Metric component"
          value={metricComponent}
          onChange={setMetricComponent}
          options={vocabularyOptions(vocabularies, 'metric_component')}
          placeholder="None"
        />
      </Container>
      <Container top={16}>
        <TextField label="Reason" value={reason} onChange={setReason} multiline rows={2} placeholder="Optional" />
      </Container>

      {error && (
        <Container top={12}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}

      <Container top={24} row verticallyCenter gap={12}>
        <FlatButton onClick={submit} disabled={!canSubmit} buttonProps={{ 'aria-disabled': !canSubmit }}>
          {busy ? 'Mapping…' : 'Map row'}
        </FlatButton>
        {!resolvedChoice && !busy && <Body7 isLowContrast>Pick an item to map to.</Body7>}
      </Container>
    </Card>
  );
};
